(() => {
  const STATS_SELECTOR = '[data-kb-fp-stats]';
  const STAT_SELECTOR = '[data-kb-fp-stat]';
  const VALUE_SELECTOR = '[data-kb-fp-value]';
  const COUNT_DURATION = 1400;
  const VALUE_PATTERN = /(\d+(?:[.,]\d+)?)/;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const parseValue = (text) => {
    const source = String(text).trim();
    const match = source.match(VALUE_PATTERN);
    if (!match || match.index == null) return null;

    const rawNumber = match[1];
    const separator = rawNumber.includes(',') ? ',' : '.';
    const numeric = Number.parseFloat(rawNumber.replace(',', '.'));
    if (!Number.isFinite(numeric)) return null;

    const hasSeparator = rawNumber.includes('.') || rawNumber.includes(',');
    const decimals = hasSeparator ? rawNumber.split(separator)[1].length : 0;

    return {
      numeric,
      decimals,
      separator,
      prefix: source.slice(0, match.index),
      suffix: source.slice(match.index + rawNumber.length),
    };
  };

  const formatValue = (parsed, current) => {
    const rounded =
      parsed.decimals > 0
        ? current.toFixed(parsed.decimals)
        : String(Math.round(current));

    const display =
      parsed.separator === ',' ? rounded.replace('.', ',') : rounded;

    return `${parsed.prefix}${display}${parsed.suffix}`;
  };

  class KalaboostFormulaProof extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._rafIds = new Map();
      this._onBlockSelect = this._handleBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      this._initFrame = requestAnimationFrame(() => this._init());
    }

    disconnectedCallback() {
      this._cleanup();
    }

    _init() {
      if (!this.isConnected || this.dataset.kbFpReady === 'true') return;

      const stats = this.querySelectorAll(STAT_SELECTOR);
      if (!stats.length) return;

      stats.forEach((stat) => this._prepareStat(stat));

      const target = this.querySelector(STATS_SELECTOR) || this;

      this._observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this._observer.disconnect();
          this._revealAll();
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );

      this._observer.observe(target);
      this.dataset.kbFpReady = 'true';
    }

    _cleanup() {
      if (this._initFrame) {
        cancelAnimationFrame(this._initFrame);
        this._initFrame = null;
      }

      this._observer?.disconnect();
      this._observer = null;

      this._rafIds?.forEach((id) => cancelAnimationFrame(id));
      this._rafIds?.clear();

      if (this._onBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._onBlockSelect = null;
      }

      this._bound = false;
      delete this.dataset.kbFpReady;
    }

    _prepareStat(stat) {
      const valueEl = stat.querySelector(VALUE_SELECTOR);
      if (!valueEl) return;

      if (!valueEl.dataset.kbFpOriginal) {
        valueEl.dataset.kbFpOriginal = valueEl.textContent.trim();
      }

      const parsed = parseValue(valueEl.dataset.kbFpOriginal);
      if (!parsed || prefersReducedMotion()) return;

      valueEl.textContent = formatValue(parsed, 0);
    }

    _revealAll() {
      this.querySelectorAll(STAT_SELECTOR).forEach((stat) => this._reveal(stat));
    }

    _reveal(stat) {
      stat.classList.add('is-visible');

      const valueEl = stat.querySelector(VALUE_SELECTOR);
      if (!valueEl) return;

      if (prefersReducedMotion()) {
        valueEl.textContent = valueEl.dataset.kbFpOriginal || valueEl.textContent;
        return;
      }

      this._count(valueEl);
    }

    _count(valueEl) {
      const original = valueEl.dataset.kbFpOriginal || valueEl.textContent.trim();
      const parsed = parseValue(original);

      if (!parsed) {
        valueEl.textContent = original;
        return;
      }

      const existing = this._rafIds.get(valueEl);
      if (existing) cancelAnimationFrame(existing);

      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / COUNT_DURATION, 1);
        const current = parsed.numeric * easeOutCubic(progress);
        valueEl.textContent = formatValue(parsed, current);

        if (progress < 1) {
          this._rafIds.set(valueEl, requestAnimationFrame(tick));
          return;
        }

        valueEl.textContent = original;
        this._rafIds.delete(valueEl);
      };

      this._rafIds.set(valueEl, requestAnimationFrame(tick));
    }

    _handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const stat = event.target.closest(STAT_SELECTOR);
      if (!stat || !this.contains(stat)) return;

      this._prepareStat(stat);
      stat.classList.remove('is-visible');

      requestAnimationFrame(() => {
        this._reveal(stat);
      });
    }
  }

  if (!customElements.get('kalaboost-formula-proof')) {
    customElements.define('kalaboost-formula-proof', KalaboostFormulaProof);
  }

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;

    const instance = event.target.matches('kalaboost-formula-proof')
      ? event.target
      : event.target.querySelector('kalaboost-formula-proof');

    instance?._cleanup?.();
  });
})();
